import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import ShoppingCart, { CartItem } from '@providers/ShoppingCart';
import { NoodleProductTypes } from '@typings/graphql-models';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import { logError } from '@providers/ErrorTracking';
import { mixpanelTrack } from '@providers/Mixpanel';
import { isChatProduct } from '@helpers/helper';
import * as format from '@format';
import X from '@components/Icons/X';
import Image from '@components/Image';

import SaaSTierIcon from '@components/SaaSTierIcon';
import useCart from '@hooks/useCart';
import getDiscountedAmount from '@helpers/getDiscountedAmount';
import classes from './CartItem.module.scss';

type Props = {
  creatorSlug: string;
  price: CartItem;
  prorationDue: number | null;
  canRemoveItems: boolean;
  tierTitle?: string | null;
  isSaaSProduct?: boolean;
  isOnlyItem?: boolean;
};

const CartItem: React.FC<Props> = ({ creatorSlug, price, prorationDue, canRemoveItems, tierTitle, isSaaSProduct, isOnlyItem }) => {
  const router = useRouter();
  const { discountCode } = useCart({ creatorSlug });
  const isChargePrice = price.product.productTypes?.some(t => t.noodleProductType === NoodleProductTypes.Charge);
  const [isRemoving, setIsRemoving] = useState(false);
  const removeItem = async (): Promise<void> => {
    setIsRemoving(true);
    await ShoppingCart.removeProduct({ price });
    mixpanelTrack('Removed item from cart', {
      priceId: price.id,
      productTitle: price.product.title,
    });
    const newCart = ShoppingCart.getCart();
    if (newCart.length === 0) {
      const { slug: productSlug } = price.product;
      if (productSlug && isChatProduct(price.product)) {
        await router.push(getUrl(IDENTIFIERS.CREATOR, { creatorSlug, tab: 'Services' }));
      } else if (price.product.includesBroadcasts) {
        await router.push(getUrl(IDENTIFIERS.CREATOR, { creatorSlug, tab: 'Broadcasts' }));
      } else if (productSlug) {
        await router.push(getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug }));
      } else {
        logError(new Error('Missing productSlug'), { priceId: price.id });
        await router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }));
      }
    }
  };

  const displayPriceText = (): string => {
    if (price.price === 0) {
      return 'Free';
    }
    if (prorationDue) {
      return `New subscription price moving forward: ${format.price.withCurrency(price.price, price.currency)}`;
    }
    return format.price.recurring(price);
  };

  const discountPrice = useMemo(() => getDiscountedAmount(price, discountCode), [price, discountCode]);

  const hasDiscount = price.price !== discountPrice;

  return (
    <div className={classes.product} style={isRemoving ? { opacity: 0.5 } : {}}>
      <div className={classes.productInfo}>
        {price.product?.image?.url && <Image className={classes.productImg} objectFit="cover" alt="" image={price.product.image.url} />}
        {isSaaSProduct && <SaaSTierIcon label={tierTitle || ''} hasCircle />}
        <div className={classes.productLabels}>
          <small>{price.product.title}</small>
          <p className={classes.productTitle}>
            {(isSaaSProduct && `Noodle ${tierTitle}`)
              || (tierTitle && price.priceTitle && `${price.priceTitle} - ${tierTitle}`)
              || (tierTitle && !price.priceTitle && tierTitle)
              || (price.priceTitle && `${price.priceTitle}`)}
          </p>
          <span className={classes.price}>
            {price.price !== 0 && price.trialPeriodDays && `Free for ${price.trialPeriodDays} days${(!isOnlyItem && ', then') || ''}`}
            {!isOnlyItem && (
              <>
                <span>
                  <span style={hasDiscount ? { textDecoration: 'line-through' } : {}}>{displayPriceText()}</span>
                  {hasDiscount
                    && ` ${format.price.recurring({
                      currency: price.currency,
                      price: discountPrice,
                      recurringInterval: price.recurringInterval,
                    })}`}
                </span>
              </>
            )}
            {price.sessionTime && (
              <>
                <span>{format.datetime.withTimezone({ datetime: price.sessionTime, timezone: price.product.creator?.timezone })}</span>
                {price.sessionDuration && <span>{format.price.durationText(price)}</span>}
                {price.product.address && <span>{price.product.address}</span>}
              </>
            )}
            {(price.numBookings || 0) > 1 && <span>{price.numBookings} services {price.sessionDuration && `(${price.sessionDuration} min each)`}{format.price.interval(price.recurringInterval)}</span>}
          </span>
        </div>
      </div>
      {!isChargePrice && canRemoveItems && (
        <button onClick={removeItem}>
          <div>
            <X size={12} color="var(--color-gray-0)" />
          </div>
        </button>
      )}
    </div>
  );
};

export default CartItem;
